import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import Dashboard from '../pages/Dashboard';
import Reports from '../pages/Reports';
import Products from '../pages/Products';
import ProductDetails from '../pages/ProductDetails';
import Patients from '../pages/Patients';
import NoPage from '../pages/NoPage';
import Orders from '../pages/Orders';
import OrderDetails from '../pages/OrderDetails';
import Invoices from '../pages/Invoices';
import InvoiceDetails from '../pages/InvoiceDetails';
import Payments from '../pages/Payments';
import PaymentDetails from '../pages/PaymentDetails';
import Settings from '../pages/Settings';
import Appointements from '../pages/Appointements';
import Services from '../pages/Services';
import HelpHome from '../pages/help/HelpHome';
import HelpCategory from '../pages/help/HelpCategory';
import HelpPost from '../pages/help/HelpPost';
import PatientDetails from '../pages/PatientDetails';
import Login from '../pages/Login';
import AccountSuspended from '../pages/AccountSuspended';
import ForgotPassword from '../pages/ForgotPassword';
import ResetPassword from '../pages/ResetPassword';
import Account from '../pages/Account';
import Landing from '../pages/Landing';
import Returns from '../pages/Returns';
import ReturnDetails from '../pages/ReturnDetails';
import Suppliers from '../pages/Suppliers';
import Pharmacies from '../pages/Pharmacies';
import PharmacyDetails from '../pages/PharmacyDetails';
import MarketingTemplates from '../pages/MarketingTemplates';
import TemplateBuilder from '../pages/TemplateBuilder';
import Campaigns from '../pages/Campaigns';
import CampaignDetail from '../pages/CampaignDetail';
import LaboratoryDetails from '../pages/LaboratoryDetails';
import AdminUsers from '../pages/AdminUsers';
import AdminRoles from '../pages/AdminRoles';
import Inventaire from '../pages/Inventaire';
import InventaireBuilder from '../pages/InventaireBuilder';
import PurchaseOrderImports from '../pages/PurchaseOrderImports';
import PurchaseOrderImportDetails from '../pages/PurchaseOrderImportDetails';
import Insurances from '../pages/Insurances';
import PrivateRoute from '../components/PrivateRoute';
import PublicRoute from '../components/PublicRoute';
import LangLayout from './LangLayout';
import LangRedirect from './LangRedirect';
import PlatformAdminRoute from '../components/PlatformAdminRoute';
import { BackofficeAuthProvider } from '../context/BackofficeAuthProvider';
import BackofficeLayout from '../layouts/BackofficeLayout';
import BackofficeLogin from '../pages/backoffice/BackofficeLogin';
import BackofficeHelpCategories from '../pages/backoffice/BackofficeHelpCategories';
import BackofficeHelpArticles from '../pages/backoffice/BackofficeHelpArticles';
import BackofficeContactMessages from '../pages/backoffice/BackofficeContactMessages';
import BackofficeIssues from '../pages/backoffice/BackofficeIssues';
import BackofficeNewsletterSubscribers from '../pages/backoffice/BackofficeNewsletterSubscribers';
import BackofficeProducts from '../pages/backoffice/BackofficeProducts';
import BackofficeSubscriptions from '../pages/backoffice/BackofficeSubscriptions';
import BackofficePharmacies from '../pages/backoffice/BackofficePharmacies';
import { PageTitleProvider } from '../layouts/PageTitleContext';
import { BreadcrumbProvider } from '../layouts/BreadcrumbContext';

const Register = lazy(() => import('../pages/Register'));
const Terms = lazy(() => import('../pages/Terms'));
const Privacy = lazy(() => import('../pages/Privacy'));
const Contact = lazy(() => import('../pages/Contact'));
const DesktopApp = lazy(() => import('../pages/DesktopApp'));
const LegalNotice = lazy(() => import('../pages/LegalNotice'));
const DeleteAccount = lazy(() => import('../pages/DeleteAccount'));
const PublicProductSearch = lazy(() => import('../pages/public/PublicProductSearch'));
const PublicProductPage = lazy(() => import('../pages/public/PublicProductPage'));

const AppRoutes = () => (
  <PageTitleProvider>
    <BreadcrumbProvider>
      <Routes>
        {/* Public/marketing pages — SEO wants one crawlable URL per language,
            so they live under /:lang instead of relying on the client-side
            language switch alone (see LangLayout). */}
        <Route path="/:lang" element={<LangLayout />}>
          <Route index element={<Landing />} />
          <Route path="contact" element={<Suspense fallback={null}><Contact /></Suspense>} />
          <Route path="desktop" element={<Suspense fallback={null}><DesktopApp /></Suspense>} />
          <Route path="terms" element={<Suspense fallback={null}><Terms /></Suspense>} />
          <Route path="privacy" element={<Suspense fallback={null}><Privacy /></Suspense>} />
          <Route path="legal-notice" element={<Suspense fallback={null}><LegalNotice /></Suspense>} />
          <Route path="delete-account" element={<Suspense fallback={null}><DeleteAccount /></Suspense>} />
          <Route path="help" element={<HelpHome />} />
          <Route path="help/:categorySlug" element={<HelpCategory />} />
          <Route path="help/:categorySlug/:postSlug" element={<HelpPost />} />
          {/* Public medicine catalog — server-rendered for crawlers by PublicProductPageController */}
          <Route path="medicaments" element={<Suspense fallback={null}><PublicProductSearch /></Suspense>} />
          <Route path="medicaments/:idSlug" element={<Suspense fallback={null}><PublicProductPage /></Suspense>} />
          <Route element={<PublicRoute />}>
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Suspense fallback={null}><Register /></Suspense>} />
          </Route>
        </Route>

        {/* Backward-compat: old unprefixed public URLs (bookmarks, links out
            in the wild) redirect to the canonical /fr/... one. */}
        <Route path="/" element={<LangRedirect />} />
        <Route path="/contact" element={<LangRedirect suffix="/contact" />} />
        <Route path="/desktop" element={<LangRedirect suffix="/desktop" />} />
        <Route path="/terms" element={<LangRedirect suffix="/terms" />} />
        <Route path="/privacy" element={<LangRedirect suffix="/privacy" />} />
        <Route path="/legal-notice" element={<LangRedirect suffix="/legal-notice" />} />
        <Route path="/delete-account" element={<LangRedirect suffix="/delete-account" />} />
        <Route path="/help" element={<LangRedirect suffix="/help" />} />
        <Route path="/help/:categorySlug" element={<LangRedirect suffix="/help" paramNames={['categorySlug']} />} />
        <Route path="/help/:categorySlug/:postSlug" element={<LangRedirect suffix="/help" paramNames={['categorySlug', 'postSlug']} />} />
        <Route path="/medicaments" element={<LangRedirect suffix="/medicaments" />} />
        <Route path="/medicaments/:idSlug" element={<LangRedirect suffix="/medicaments" paramNames={['idSlug']} />} />
        <Route path="/login" element={<LangRedirect suffix="/login" />} />
        <Route path="/register" element={<LangRedirect suffix="/register" />} />

        <Route path="/account-suspended" element={<AccountSuspended />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route element={<PrivateRoute />}>
          <Route element={<MainLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/products" element={<Products />} />
            <Route path="/products/:id" element={<ProductDetails />} />
            <Route path="/patients" element={<Patients />} />
            <Route path="/patient/:id/:tab?" element={<PatientDetails />} />
            <Route path="/orders" element={<Orders />} />
            <Route path="/order/:id/:tab?" element={<OrderDetails />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/invoices/:id/:tab?" element={<InvoiceDetails />} />
            <Route path="/payments" element={<Payments />} />
            <Route path="/payments/:id" element={<PaymentDetails />} />
            <Route path="/services" element={<Services />} />
            <Route path="/Appointements" element={<Appointements />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/account/:tab?" element={<Account />} />
            <Route path="/returns" element={<Returns />} />
            <Route path="/returns/:id" element={<ReturnDetails />} />
            <Route path="/suppliers" element={<Suppliers />} />
            <Route path="/suppliers/:id" element={<LaboratoryDetails />} />
            <Route path="/confreres" element={<Pharmacies />} />
            <Route path="/confreres/:id" element={<PharmacyDetails />} />
            <Route path="/marketing/templates" element={<MarketingTemplates />} />
            <Route path="/marketing/templates/new" element={<TemplateBuilder />} />
            <Route path="/marketing/templates/:id/edit" element={<TemplateBuilder />} />
            <Route path="/marketing/campaigns" element={<Campaigns />} />
            <Route path="/marketing/campaigns/:id" element={<CampaignDetail />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/roles" element={<AdminRoles />} />
            <Route path="/inventaire" element={<Inventaire />} />
            <Route path="/inventaire/:id" element={<InventaireBuilder />} />
            <Route path="/purchase-order-imports" element={<PurchaseOrderImports />} />
            <Route path="/purchase-order-imports/:id" element={<PurchaseOrderImportDetails />} />
            <Route path="/insurances" element={<Insurances />} />
          </Route>
        </Route>

        {/* Platform-owner-only backoffice — own auth realm (PlatformAdmin, see
            BackofficeAuthProvider/PlatformAdminRoute), no pharmacy Sidebar, not tied
            to any pharmacy User account. */}
        <Route element={<BackofficeAuthProvider />}>
          <Route path="/backoffice/login" element={<BackofficeLogin />} />
          <Route element={<PlatformAdminRoute />}>
            <Route element={<BackofficeLayout />}>
              <Route path="/backoffice" element={<Navigate to="/backoffice/help" replace />} />
              <Route path="/backoffice/help" element={<BackofficeHelpCategories />} />
              <Route path="/backoffice/help/:categoryId" element={<BackofficeHelpArticles />} />
              <Route path="/backoffice/contact-messages" element={<BackofficeContactMessages />} />
              <Route path="/backoffice/issues" element={<BackofficeIssues />} />
              <Route path="/backoffice/newsletter-subscribers" element={<BackofficeNewsletterSubscribers />} />
              <Route path="/backoffice/products" element={<BackofficeProducts />} />
              <Route path="/backoffice/subscriptions" element={<BackofficeSubscriptions />} />
              <Route path="/backoffice/pharmacies" element={<BackofficePharmacies />} />
            </Route>
          </Route>
        </Route>

        <Route path="*" element={<NoPage />} />
      </Routes>
    </BreadcrumbProvider>
  </PageTitleProvider>
);

export default AppRoutes;
